import { useMemo } from 'react';
import styled from 'styled-components';

import { Button } from 'shared/components/Button';
import { BuyBarGlobalPricingInfo } from 'productTemplate/components/buyBar/styles/BuyBarStyles';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { setIsWantsWindowOpen, setTempUserWant } from 'store/slice/pdpSlice';
import { VARIANT_KEYS } from 'productTemplate/enums/variantKeys';
import { VariantPriceListRows } from 'productTemplate/components/buyBar/VariantPriceListRows';

const TP = 'productTemplate.components.buyBar.BigBuyBarDesktopNewSection';

interface IBigBuyBarDesktopNewSection {
  size: number;
}

export const BigBuyBarDesktopNewSection = ({ size }: IBigBuyBarDesktopNewSection) => {
  const { mapSizeToVariants } = useStoreSelector((state) => state.pdp.buyBarVariants);
  const { userWants } = useStoreSelector((state) => state.pdp);
  const storeDispatch = useStoreDispatch();

  const variants = mapSizeToVariants?.[size];

  const bestPrice = variants ? variants?.[VARIANT_KEYS.BEST_PRICE] : null;
  const instantShip = variants ? variants?.[VARIANT_KEYS.INSTANT_SHIP] : null;
  const newWithBoxIssues = variants ? variants?.[VARIANT_KEYS.NEW_WITH_BOX_ISSUES] : null;
  const newWithDefects = variants ? variants?.[VARIANT_KEYS.NEW_WITH_DEFECTS] : null;

  const showNewWithBoxIssues =
    newWithBoxIssues &&
    (!bestPrice ||
      newWithBoxIssues?.lowestPriceCents?.amountUsdCents <
        bestPrice?.lowestPriceCents?.amountUsdCents);

  const wantMap = useMemo(
    () => userWants?.reduce((acc, want) => acc.add(want.size), new Set<number>()),
    [userWants],
  );
  const wanted = wantMap.has(size);
  const handleOutOfStockClick = () => {
    storeDispatch(setIsWantsWindowOpen(true));
    storeDispatch(setTempUserWant(size));
  };

  if (!bestPrice && !newWithBoxIssues && !newWithDefects) {
    return (
      <BigBuyBarDesktopNewSection.NoStockAddToWants
        data-qa="oos_wrapper"
        onClick={handleOutOfStockClick}
      >
        <span className="title">{t(`${TP}.currentlyUnavailable`, 'Currently Unavailable')}</span>
        <span>
          {t(
            `${TP}.addToYourWants`,
            'Add this item to your Wants to be notified once it’s back in stock',
          )}
        </span>
        <Button
          data-qa="oos_want_btn"
          title="Want"
          className={wanted ? 'wanted' : 'unwanted'}
          inverted={!wanted}
        />
      </BigBuyBarDesktopNewSection.NoStockAddToWants>
    );
  }

  return (
    <>
      {variants && Object.keys(variants)?.length > 0 && (
        <BuyBarGlobalPricingInfo location="new_buy_bar" />
      )}
      <BigBuyBarDesktopNewSection.Table>
        <VariantPriceListRows
          bestPriceVariant={bestPrice}
          instantShipVariant={instantShip}
          newWithBoxIssuesVariant={newWithBoxIssues}
          showNewWithBoxIssues={showNewWithBoxIssues}
          newWithDefects={newWithDefects}
        />
      </BigBuyBarDesktopNewSection.Table>
    </>
  );
};

BigBuyBarDesktopNewSection.Table = styled.table`
  width: 100%;
  margin-top: 32px;
`;

BigBuyBarDesktopNewSection.NoStockAddToWants = styled.div`
  width: 100%;
  display: flex;

  align-items: center;
  justify-content: space-between;

  font-size: 12px;
  margin: 24px 0;

  .title {
    margin: 0;
    font-size: 11px;
    line-height: 11px;
  }

  &:hover {
    cursor: pointer;

    .title {
      text-decoration: underline;
    }

    .wanted {
      background-color: ${({ theme }) => theme.colors.white};
      color: ${({ theme }) => theme.colors.black};
      border: ${({ theme }) => theme.navigation.border};
    }

    .unwanted {
      background-color: ${({ theme }) => theme.colors.black};
      color: ${({ theme }) => theme.colors.white};
      border: ${({ theme }) => theme.navigation.invertedBorder};
    }
  }

  & span {
    font-size: 10.5px;
    margin-left: 24px;
  }

  & button {
    display: inline-flex;

    height: 19px;
    max-width: 35px;
    font-size: 11px;
    line-height: 11px;

    text-transform: none;

    span {
      margin: 0;
    }
  }
`;

BigBuyBarDesktopNewSection.PriorityProcessing = styled.span`
  padding: 0;
  text-align: left;
  text-decoration: underline;
`;
